import type {CouponData} from '../../../types';

import {CloseOutlined, InfoCircleOutlined} from '@ant-design/icons';
import {CountdownTimer} from '@countdown-timer';
import {BottomSheet, Button, IconButton, OtpInput} from '@ds/components';
import {toast} from '@Toast';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch} from 'store';
import {confirmCouponPreorder} from 'store/slices/coupon/thunk';

import {COUPON_ROUTES} from '../../../constants';

type CouponOtpSheetProps = {
  vendorId: string;
  show: boolean;
  onClose: () => void;
  onResend: () => Promise<boolean>;
};

const OTP_COUNTDOWN_SECONDS = 120;

const CouponOtpSheet: React.FC<CouponOtpSheetProps> = ({vendorId, show, onClose, onResend}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [countDown, setCountDown] = useState(OTP_COUNTDOWN_SECONDS);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (show) {
      setOtp('');
      setCountDown(OTP_COUNTDOWN_SECONDS);
    }
  }, [show]);

  const confirmOtp = async () => {
    if (otp.length !== 5) {
      toast('لطفا کد پیامک شده را وارد کنید');
      return;
    }

    setIsConfirming(true);
    try {
      const response = await dispatch(
        confirmCouponPreorder({vendor_id: vendorId, code: otp})
      ).unwrap();
      navigate(COUPON_ROUTES.paymentSuccess, {
        state: response.data as CouponData
      });
    } catch {
      toast('کد اشتباه است');
    } finally {
      setIsConfirming(false);
    }
  };

  const resendOtp = async () => {
    setIsResending(true);
    try {
      const wasSent = await onResend();
      if (wasSent) {
        setOtp('');
        setCountDown(OTP_COUNTDOWN_SECONDS);
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <BottomSheet
      show={show}
      onClose={onClose}
      header={
        <div className="flex items-center gap-2 px-2 pt-2">
          <IconButton aria-label="بستن" size="md" onClick={onClose}>
            <CloseOutlined aria-hidden />
          </IconButton>
          <h2 className="text-subtitle-md text-neutral-fg">دریافت رمز یکبار مصرف</h2>
        </div>
      }
      footer={
        <div className="flex justify-end border-t border-gray-04 p-2.5">
          <Button onClick={() => void confirmOtp()} loading={isConfirming}>
            تایید پرداخت
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <p className="text-body-sm text-neutral-fg">
          برای تایید پرداخت، رمز یک‌بارمصرف ارسال شده را وارد کنید.
        </p>
        <OtpInput length={5} value={otp} onChange={setOtp} />
        <div className="flex items-center justify-center gap-2.5 text-body-sm text-gray-08">
          {countDown > 0 ? (
            <>
              <span>درخواست دوباره رمز تا</span>
              <CountdownTimer initialCounter={countDown} setCountDown={setCountDown} />
            </>
          ) : (
            <>
              <span>کد را دریافت نکردید؟</span>
              <Button
                className="px-0!"
                variant="link"
                color="primary"
                size="sm"
                loading={isResending}
                onClick={() => void resendOtp()}
              >
                ارسال دوباره کد
              </Button>
            </>
          )}
        </div>
        <div className="flex gap-2.5 rounded-xl bg-blue-mist p-4 text-body-sm text-neutral-fg">
          <InfoCircleOutlined aria-hidden className="mt-1 shrink-0 text-body-lg" />
          <p>مبلغ قابل پرداخت در زمان تسویه در اولین موعد از فاکتور شما کسر می‌گردد.</p>
        </div>
      </div>
    </BottomSheet>
  );
};

export default CouponOtpSheet;
